import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import Sound from "react-sound";
import { s3base } from "api.js";
import NoMicrophone from "views/NoMicrophone.jsx";
import ReusableRecorder from "components/Recorder/ReusableRecorder";
import Beep from "Beep.js";
import "components/custom.css";
import MicWaveform from 'components/Question/Speaking/MicWaveform';
import RecordedWaveform from "components/Question/Speaking/RecordedWaveform";
import { isIOSDevice } from 'util/deviceDetection';
import useBeep from 'components/BeepSound';

const DescribeImage = (props) => {
  const [state, setState] = useState({
    isRecording: false,
    blobURL: "",
    isBlocked: false,
    question: "",
    secondCount: 25,
    recordCount: 0,
    recordId: 1,
    intervalId: 0,
    duration: 0,
    questionId: "",
    reset: false,
    trans: "",
    correctFlag: true,
    soundStatus: false,
    file: "",
    imageLoaded: true,
    micPermitted: null,
    browserAllowed: null,
    question_image: null,
    skip: false,
  });

  const recorderRef = useRef(null);
  const submittedRef = useRef(false);
  const recordingStartedRef = useRef(false); // Prevent double-start
	const { playBeep } = useBeep({ frequency: 800, duration: 300, volume: 0.3 });

  const {
    micPermitted,
    browserAllowed,
    question_image,
    blobURL,
    recordCount,
    saveTranscript,
    soundStatus,
  } = state;
  const { resultMode } = props;

  useEffect(() => {
    if(resultMode){
      setState((prevState) => ({
          ...prevState,
          isBlocked: false,
          micPermitted: true,
          question_image: `data:image/png;base64, ${props.data.image}`,
          reset: false,
        }));
      return
    }
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(() => {
        // Microphone Permission Granted.
        setState((prevState) => ({
          ...prevState,
          isBlocked: false,
          micPermitted: true,
          // browserAllowed: chrome,
          question_image: `data:image/png;base64, ${props.data.image}`,
          reset: false,
        }));
      })
      .catch(() => {
        // Permission Denied
        setState((prevState) => ({
          ...prevState,
          isBlocked: true,
          micPermitted: false,
          // browserAllowed: true,
        }));
      });

    return () => {
      clearInterval(state.intervalId);
    };
  }, [props.data.image]);

  // const startProcess = useCallback(() => {
  //   if (!props.answer) {
  //     const intervalId = setInterval(() => {
  //       setState(prevState => {
  //         let state_obj = {};
  //         if (prevState.secondCount === 1) {
  //           clearInterval(prevState.intervalId);
  //           setState(prev => ({ ...prev, soundStatus: true }));
  //           const recordId = setInterval(() => {
  //             setState(p => {
  //               if (p.recordCount === 40) {
  //                 clearInterval(p.recordId);
  //                 // stop recording logic here
  //               }
  //               return { ...p, recordCount: p.recordCount + 1 };
  //             });
  //           }, 1000);
  //           state_obj.recordId = recordId;
  //           state_obj.skip = false;
  //         }
  //         state_obj.secondCount = prevState.secondCount - 1;
  //         return { ...prevState, ...state_obj };
  //       });
  //     }, 1000);
  //     setState((prevState) => ({
  //       ...prevState,
  //       intervalId,
  //       imageLoaded: true,
  //     }));
  //   }
  // }, [props.answer]);


  useEffect(() => {
    if (props.data) {
      if (
        (state.questionId && state.questionId !== props.data.id) ||
        props.restart
      ) {
        clearInterval(state.intervalId);
        clearInterval(state.recordId);

        recordingStartedRef.current = false; // Reset on question change

        setState((prevState) => ({
          ...prevState,
          recordCount: 0,
          isRecording: false,
          secondCount: 25,
          questionId: props.data.id,
          blobURL: "",
          reset: true,
          imageLoaded: false,
          trans: "",
          duration: 0,
        }));
      } else if (!state.questionId) {
        setState((prevState) => ({
          ...prevState,
          questionId: props.data.id,
          imageLoaded: false,
        }));
      }
    }
  }, [props.data, props.restart, state.questionId, state.intervalId, state.recordId]);

  // Handle restart like ReadAloud does
  useEffect(() => {
    if (props.restart) {
      props.restartQue();
    }
  }, [props.restart]);

  // Start recording immediately — called the moment the beep begins playing
  const startRecordingNow = useCallback(() => {
    if (recordingStartedRef.current) return; // Guard against double calls
    recordingStartedRef.current = true;

    setState(prevState => {
      if (prevState.isBlocked) return prevState;

      if (recorderRef.current) {
        recorderRef.current.startRecording();
      }

      const recordId = setInterval(() => {
        setState(p => {
          if (p.recordCount === 40) {
            clearInterval(p.recordId);
            if (!p.isBlocked && p.isRecording && recorderRef.current) {
              recorderRef.current.stopRecording();
            }
          }
          return { ...p, recordCount: p.recordCount + 1 };
        });
      }, 1000);

      return {
        ...prevState,
        recordId,
        isRecording: true,
        blobURL: "",
      };
    });
  }, []);

  // Recording already started when beep began — just clear beep status
  const handleSongFinishedPlaying = useCallback(() => {
    setState(prevState => ({ ...prevState, soundStatus: false }));
  }, []);

  const handleRecordingComplete = useCallback((recordingData) => {
    const { blob, url, transcript, duration: recordDuration, file } = recordingData;
    
    setState((prev) => ({
      ...prev,
      blobURL: url,
      isRecording: false,
      recordCount: 100,
      file,
      duration: recordDuration,
      trans: transcript || ""
    }));

    const data_to_send = {
      data: file,
      audio_text: transcript || "",
      duration: recordDuration,
      blob_url: url
    };

    if (isIOSDevice() && !submittedRef.current) return;

    if (props.testMode) {
      props.nextQuestion(data_to_send);
    } else {
      props.submit(data_to_send, true);
      props.showResult();
    }
  }, [props]);

  const stop = useCallback(() => {
    if (recorderRef.current && state.isRecording) {
      recorderRef.current.stopRecording();
    }
  }, [state.isRecording]);

  const updateCorrect = useCallback(() => {
    props.submit({
      data: "",
      audio_text: "",
      duration: state.duration,
    });
    setState((prevState) => ({ ...prevState, correctFlag: false }));
  }, [props, state.duration]);

  const skip = useCallback(() => {
    setState(prevState => {
      clearInterval(prevState.intervalId);

      if (!prevState.isBlocked && recorderRef.current) {
        recorderRef.current.startRecording();
      }

      const recordId = setInterval(() => {
        setState(p => {
          if (p.recordCount === 40) {
            clearInterval(p.recordId);
            if (recorderRef.current) {
              recorderRef.current.stopRecording();
            }
          }
          return { ...p, recordCount: p.recordCount + 1 };
        });
      }, 1000);

      return {
        ...prevState,
        recordId,
        secondCount: 0,
        skip: false,
        soundStatus: true,
        isRecording: true,
        blobURL: ""
      };
    });
  }, []);

  const handleImageLoaded = useCallback(() => {

    if (!props.answer) {
      recordingStartedRef.current = false; // Reset for new image load

      const intervalId = setInterval(() => {
        setState(prevState => {
          let state_obj = {};
          if (prevState.secondCount === 1) {
            clearInterval(prevState.intervalId);
            state_obj.skip = false;
            // Trigger beep — recording starts immediately when beep begins
            // (handled in startRecordingNow via handlePlayBeep/soundStatus)
            return { ...prevState, ...state_obj, soundStatus: true };
          }

          state_obj.secondCount = prevState.secondCount - 1;
          return { ...prevState, ...state_obj };
        });
      }, 1000);
      setState((prevState) => ({
        ...prevState,
        intervalId,
        imageLoaded: true,
        skip: true,
      }));
    }
  }, [props.answer]);

  const handlePlayBeep = useCallback(() => {
    playBeep({
      onStart: () => {
        setState(prev => ({ ...prev, soundStatus: true }));
        // Start recording immediately when beep begins so no audio is missed
        startRecordingNow();
      },
      onComplete: () => {
        // Recording already running — just clear beep status
        setState(prev => ({ ...prev, soundStatus: false }));
      },
      onError: (error) => {
        console.error('Beep failed:', error);
        setState(prev => ({ ...prev, soundStatus: false }));
      }
    });
  }, [playBeep, startRecordingNow]);

  useEffect(() => {
    if (isIOSDevice() && soundStatus) {
      handlePlayBeep();
    }
  }, [soundStatus, handlePlayBeep]);

  // Non-iOS: when soundStatus becomes true, start recording immediately
  // (the Sound component plays the beep; we start recording at the same time)
  useEffect(() => {
    if (!isIOSDevice() && soundStatus && !resultMode) {
      startRecordingNow();
    }
  }, [soundStatus, resultMode, startRecordingNow]);

  const recordingLabelText = (() => {
    const { blobURL, isRecording, secondCount } = state;
    if (blobURL === "" && !isRecording) return `Recording starts in ${secondCount} seconds...`;
    if (isRecording) return "Recording started";
    if (!isRecording && blobURL !== "") return "Recording finished";
    return "";
  })();

  useEffect(() => {
    if (props.restart) {
      props.restartQue();
    }
  }, [props.restart]);

  // Handle stopRecording
  useEffect(() => {
    if (props.stopRecording) {
      console.log('DescribeImage: stopRecording detected');
      submittedRef.current = true;
      if (state.isRecording && recorderRef.current) {
        recorderRef.current.stopRecording();
      } else {
        const data_to_send = {
          data: state.file,
          audio_text: state.trans,
          duration: state.duration,
          blob_url: state.blobURL
        };
        props.nextQuestion(data_to_send);
      }
    }
  }, [props.stopRecording, state.isRecording, state.file, state.trans, state.duration, state.blobURL]);

  useEffect(() => {
    if (state.correctFlag) {
      updateCorrect();
    }
  }, [state.correctFlag]);

  if (state.isBlocked) {
    return <NoMicrophone />;
  }

  const showSkipWaitButtonFlag = state.skip && !props.testMode && state.blobURL === "" && !state.isRecording;
  const showStopButtonFlag = !props.testMode && state.isRecording && recordCount !== 0;
  const showRecordedAudioFlag = state.blobURL && !props.testMode;

  return (
    <>
      {micPermitted ? (
        <>
          <ReusableRecorder
            ref={recorderRef}
            onRecordingComplete={handleRecordingComplete}
            maxDuration={40000}
            enableSpeechRecognition={true}
            fileName={"noname1.mp3"}
          />

          {!isIOSDevice() && !resultMode && soundStatus && (
            <Sound
              url={Beep}
              playStatus={Sound.status.PLAYING}
              onFinishedPlaying={handleSongFinishedPlaying}
              onError={handleSongFinishedPlaying}
            />
          )}

          <div className="pmw-tabs-section d-flex align-items-center pmw-outer-wrapper recorded-wrapper logging-system visibility-visible m-0 pb-5 pb-md-0 mock-container">
            {!resultMode && (
              <>
              <div className="recorded-section describe-image h-100">
                <div className="recorded-content">
                  <div className="img-wrapper">
                    <img
                      alt="media-link"
                      width="500"
                      onLoad={handleImageLoaded}
                      src={question_image}
                    />
                  </div>
                </div>
              </div>
                <div className="recorded-section h-100 justify-content-center mock-resource-text">
                  <div className="recorded-content">
                    <ul className="recorded-list">
                      <li>Status:</li>
                      <li>{recordingLabelText}</li>
                    </ul>
                    <div className="recorded-audio-wrapper">
                      {showRecordedAudioFlag ? (
                        <RecordedWaveform
                          testMode={false}
                          blobURL={state.blobURL}
                        />
                      ) : (
                        <>
                          {recordCount === 0 ? (
                            // <img src="/images/waveform.png" alt="Waveform" />
                            <div className="mic-waveform-wrapper-read" style={{ marginTop: "48px" }}>
                            <div className="waveform">
                                {Array.from({ length: 100 }).map((_, i) => (
                                  <div key={i} className="wave-bar" />
                                ))}
                              </div>
                            </div>
                          ) : (
                            <MicWaveform
                              progressValue={recordCount * 2.5}
                              striped
                            />
                          )}
                          {showSkipWaitButtonFlag && (
                            <div className="btn-outer mt-0 mt-md-4">
                              <button
                                className="skip-btn visibility-visible"
                                onClick={skip}
                              >
                                Skip
                              </button>
                            </div>
                          )}
                          {showStopButtonFlag && (
                            <div className="btn-outer mt-0 mt-md-4">
                              <button
                                className="skip-btn visibility-visible"
                                onClick={stop}
                              >
                                Stop
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      ) : (
        <div className="pmw-tabs h-100">
          {micPermitted === false && (
            <div className="alert alert-danger">
              No Microphone permissions granted. Please grant microphone
              permissions and reload the page.
            </div>
          )}
          {/* {browserAllowed === false && (
            <div className="alert alert-danger">
              The browser you are using is not supported. Please use Google
              Chrome.
            </div>
          )} */}
        </div>
      )}
    </>
  );
};

export default DescribeImage;


